import {orderBy} from "lodash";
import {action, makeAutoObservable, observable} from "mobx";
import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {Bindings} from "data/constants/bindings";
import type {IDivision, IDivisionsStore} from "data/stores/divisions/divisions.store";
import type {
	IMatchFilter,
	IMatchFilterStore,
} from "data/stores/match_filter_store/match_filter_store.store";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import {SelectChangeEvent} from "@mui/material";
import type {ISquad, ISquadsStore} from "data/stores/squads/squads.store";

export interface IMatchFilterController extends ViewController {
	readonly i18n: ILocalizationStore;

	get isOpen(): boolean;
	get divisions(): IDivision[];
	get filter(): IMatchFilter;
	get squadsByDivision(): ISquad[];

	handleSelectChange: (event: SelectChangeEvent<string>) => void;
	closeFilter: () => void;
	openFilter: () => void;
}

@injectable()
export class MatchFilterController implements IMatchFilterController {
	@observable private _isOpen: boolean = true;

	get isOpen() {
		return this._isOpen;
	}

	get divisions() {
		return orderBy(this._divisionStore.divisions, "name");
	}

	get filter() {
		return this._matchFilterStore.filter;
	}

	get squadsByDivision() {
		const list = orderBy(this._squadsStore.list, "name");

		if (Number(this._matchFilterStore.filter.division)) {
			return list.filter(
				(squad) => squad.divisionId === Number(this._matchFilterStore.filter.division)
			);
		}

		return list;
	}
	constructor(
		@inject(Bindings.LocalizationStore) readonly i18n: ILocalizationStore,
		@inject(Bindings.DivisionsStore) private _divisionStore: IDivisionsStore,
		@inject(Bindings.MatchFilterStore) private _matchFilterStore: IMatchFilterStore,
		@inject(Bindings.SquadsStore) private _squadsStore: ISquadsStore
	) {
		makeAutoObservable(this);
	}

	@action handleSelectChange = (event: SelectChangeEvent<string>) => {
		const {value, name} = event.target;

		this._matchFilterStore.setFilter(name as keyof IMatchFilter, value);

		if (name === "division") {
			this._matchFilterStore.setFilter("squad", "0");
		}
	};

	@action closeFilter = () => {
		this._isOpen = false;
	};

	@action openFilter = () => {
		this._isOpen = true;
	};
}
